// The numbers strip on top of the Assistant tabs (app-feel plan, builder B).
//
// ⚠️ THE SAME COUNTS AS THE TABS. The Inbox counts CLIENTS active in the period
// (groupByClient, then splitByPeriod on their latest activity); the Pipeline
// counts active orders (not delivered, with an order) in the period, their
// money, and those stuck on OUR side. A strip that disagreed with the list
// under it would teach the manager to trust neither.
import type { InboxItem, PipelineClient } from '@/lib/api';
import { groupByClient, latestIso, splitByPeriod } from './workbench';

export type Range = { from?: string; to?: string };

export function inboxNumbers(items: InboxItem[], range: Range, gone: Set<string> = new Set()): { now: number; today: number } {
  const rows = splitByPeriod(groupByClient(items.filter((i) => !gone.has(i.key))),
    (r) => latestIso(r.items.map((i) => i.activityIso)), range).shown;
  return {
    now: rows.filter((r) => r.tier === 'now').length,
    today: rows.filter((r) => r.tier === 'today').length,
  };
}

/** The orders being made: in the period, on the board, with an order, not yet delivered. */
export function activeOrders(clients: PipelineClient[]): PipelineClient[] {
  return clients.filter((c) => c.active && !c.ignored && c.primary && c.stage && c.stage !== 'delivered');
}

export function pipelineNumbers(clients: PipelineClient[], range: Range): { orders: number; money: number; stuckOurs: number } {
  const shown = splitByPeriod(clients, (c) => c.lastActivityIso, range).shown;
  const active = activeOrders(shown);
  return {
    orders: active.length,
    money: active.reduce((s, c) => s + c.money, 0),
    stuckOurs: active.filter((c) => c.stuck && c.ballWith === 'ours').length,
  };
}

/** Where a card goes when tapped — a tab, and maybe its sub-view. */
export interface StripTarget { tab: 'inbox' | 'pipeline'; sub?: string }
export interface StripCard {
  key: 'stuck' | 'now' | 'today' | 'orders' | 'money';
  label: string;
  value: number;
  /** Money is shown as « 2 800 MAD ». */
  money?: boolean;
  /** alert = red (needs a human now), warn = amber (slower than it should be), calm = grey. */
  tone: 'alert' | 'warn' | 'calm';
  to: StripTarget;
}

/** STUCK first (owner: what is slow on our side is the first thing to see). */
export function stripCards(inbox: { now: number; today: number }, pipe: { orders: number; money: number; stuckOurs: number }): StripCard[] {
  return [
    { key: 'stuck', label: 'Bloquées chez nous', value: pipe.stuckOurs, tone: pipe.stuckOurs > 0 ? 'warn' : 'calm', to: { tab: 'pipeline', sub: 'stuck' } },
    { key: 'now', label: 'À traiter maintenant', value: inbox.now, tone: inbox.now > 0 ? 'alert' : 'calm', to: { tab: 'inbox' } },
    { key: 'today', label: 'Aujourd’hui', value: inbox.today, tone: 'calm', to: { tab: 'inbox' } },
    { key: 'orders', label: 'Commandes en cours', value: pipe.orders, tone: 'calm', to: { tab: 'pipeline' } },
    { key: 'money', label: 'En cours', value: pipe.money, money: true, tone: 'calm', to: { tab: 'pipeline' } },
  ];
}
